import { useCallback, useState } from 'react'
import { ButtonPrimary } from '../../../../../../../baseComponents/Button'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import { InputNumberWithLabelMargin, InputWithLabelMargin } from '../../../../../../../forms/Input'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { RowData } from '../../../../../../../baseComponents/TwinTable/types'
import { faXmark } from '@fortawesome/pro-light-svg-icons'
import TwinIcon from '../../../../../../../baseComponents/TwinIcon'
import { productTypesInversed, ProductTypesKeys } from '@teinor/erp/types/company/product'
import { OrderUniqueNumber } from '@teinor/erp/types/company/order/general'
import { changeErrorMessage } from '../../../../../../../utils/reducers/reduxDispatch'
import { LittleModalCreateEdit } from '../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { BeforeSubmitHandler } from '../../../../../../../forms/TwinForm/types'
import { InputTableURLsTable } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { ModalEditComponentProps } from '../../../../../../../baseComponents/ModalsLayouts/types'

type ModalUniqueNumberProductProps = ModalEditComponentProps & ModalUniqueNumberProductLogicProps & {
    title: string
    inputTableURLs: InputTableURLsTable
}

const ModalUniqueNumberProduct: React.FC<ModalUniqueNumberProductProps> = ({ title, setOpened, onSubmit, allRowData, productType, inputTableURLs,  ...logic }) => {
    const {t} = useTwinTranslation()
    const { uniqueNumber, updateUniqueNumberValue, addInputSerial, deleteUniqueNumber, checkAllUniqueNumberOk } = useModalUniqueNumberProductLogic({ allRowData, productType,  ...logic })
    const readOnly = allRowData?.BuyDeliveryNoteProductId
    
    const renderThis: JSX.Element[] = []
    for (const key in uniqueNumber) {
        const index = parseInt(key)
        renderThis.push(<InputUniqueNumberLot key={index} {...{ index, readOnly, allRowData, uniqueNumber, updateUniqueNumberValue, deleteUniqueNumber, productType }}  />)
    }
    return ( 
        <LittleModalCreateEdit url={inputTableURLs.updateData} setOpened={setOpened} onSubmit={onSubmit} translations={{
            title,
            button: t('save', 'Guardar')
        }} haveButtonPermissions={true} beforeSubmit={checkAllUniqueNumberOk} fields={[]}>
            {renderThis}
            {!readOnly && productType === productTypesInversed['lots'] ? <ButtonUniqueNumber {...{ addInputSerial }} /> : null}
        </LittleModalCreateEdit>
    )
}

interface ModalUniqueNumberProductLogicProps {
    allRowData?: RowData
    productType: ProductTypesKeys
    WarehouseId: number
}

const useModalUniqueNumberProductLogic = ({ allRowData, productType, WarehouseId }: ModalUniqueNumberProductLogicProps) => {
    const {t} = useTwinTranslation()
    const errorMessageUnique = t('twoUniqueNumbersDuplicated', 'Dos números de serie coinciden')
    const errorMessageQuantity = t('notMatchQuantity', 'Las cantidades no coinciden')
    const unique_numbers = allRowData?.unique_number|| []
    const [uniqueNumber, setUniqueNumber] = useState<OrderUniqueNumber[]>(()=>{
        if (productType === productTypesInversed['serialized']){
            const arr = unique_numbers.length > allRowData?.quantity ? [] : Array(parseInt(allRowData?.quantity) - unique_numbers.length).fill({ serialCode: '', quantity: 1, WarehouseId })
            return [...unique_numbers, ...arr]
        }
        return unique_numbers?.length ? [...unique_numbers] : [{ serialCode: '', WarehouseId, quantity: parseFloat(allRowData?.quantity) }]
    })

    const updateUniqueNumberValue = useCallback((value: string, index: number, field: 'serialCode' | 'quantity') => {
        setUniqueNumber((old)=> {
            const uniqueNumberCopy = JSON.parse(JSON.stringify(old))
            uniqueNumberCopy[index][field] = value
            return[...uniqueNumberCopy]
        })
    }, [setUniqueNumber])

    const checkAllUniqueNumberOk: BeforeSubmitHandler = useCallback((vals) => {
        const match: { [serialCode: string]: number[] } = {}
        const err = []
        let totalQuantity = 0
        let myUniqueNumbers: OrderUniqueNumber[] = []
        for (const key in uniqueNumber) {
            const unique_number = uniqueNumber[key]
            if(unique_number.serialCode){
                myUniqueNumbers.push(unique_number)
                const serialCode = unique_number.serialCode
                totalQuantity += parseFloat(String(unique_number.quantity || 1))
                const keyParsed = parseInt(key)
                if (!match[serialCode]) {
                    match[serialCode] = []
                } else {
                    err.push(keyParsed)
                }
                match[serialCode].push(keyParsed)
            }
        }
        if(totalQuantity !== parseFloat(allRowData?.quantity)){
            changeErrorMessage(errorMessageQuantity)
            return false
        }
        if(err.length){
            changeErrorMessage(errorMessageUnique)
            return false
        }
        vals.id = allRowData?.id
        vals.unique_number = myUniqueNumbers
        return true
    }, [uniqueNumber, errorMessageUnique, allRowData, errorMessageQuantity])

    const addInputSerial = useCallback(() => {
        setUniqueNumber((old)=>{
            const uniqueNumberCopy = JSON.parse(JSON.stringify(old))
            let totalQuantity = 0
            for (const key in uniqueNumberCopy) {
                const quantityUniqueNumber = uniqueNumberCopy[key].quantity
                totalQuantity += quantityUniqueNumber
            }
            const myQuantity = parseFloat(allRowData?.quantity) - totalQuantity
            uniqueNumberCopy.push({ serialCode: '', quantity: myQuantity > 0 ? myQuantity : 0, WarehouseId })
            return [...uniqueNumberCopy]
        })
    }, [setUniqueNumber, allRowData?.quantity, WarehouseId ])

    const deleteUniqueNumber = useCallback((pos: number) => {
        setUniqueNumber((old)=>{
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld.splice(pos, 1)
            return [...copyOld]
        })
    }, [setUniqueNumber])

    return {uniqueNumber, updateUniqueNumberValue, addInputSerial, deleteUniqueNumber, checkAllUniqueNumberOk }
}


interface InputUniqueNumberLotProps {
    index: number
    productType: ProductTypesKeys
    uniqueNumber: OrderUniqueNumber[]
    readOnly?: boolean
    deleteUniqueNumber: (pos: number)=> void
    updateUniqueNumberValue: (value: string, index: number, field: 'serialCode' | 'quantity') => void
}

const InputUniqueNumberLot: React.FC<InputUniqueNumberLotProps> = ({ index, uniqueNumber, updateUniqueNumberValue, deleteUniqueNumber, productType, readOnly }) => {
    const {t} = useTwinTranslation()
    const isLot = productType === productTypesInversed['lots']
    return (
        <div className='flex justify-start items-center w-full'>
            {isLot ? <InputNumberWithLabelMargin className='w-55' label={t('quantity', 'Cantidad')} value={uniqueNumber[index].quantity || ''} onChange={(value) => updateUniqueNumberValue(value, index, 'quantity')} {...{readOnly}} /> : null}
            <InputWithLabelMargin className={'w-full my-10 text-left ' + (isLot ? 'ml-40 ' : '') } label={isLot ? t('lotNumber', 'Número de Lote') : t('uniqueNumer', 'Número de Serie')} value={uniqueNumber[index].serialCode} onChange={(value) => updateUniqueNumberValue(value, index, 'serialCode')} {...{ readOnly }} />
            {!readOnly ? <TwinIcon className='ml-5 cursor-pointer' icon={faXmark} onClick={() => deleteUniqueNumber(index)} /> : null}
        </div>
    )
}

interface ButtonUniqueNumberProps {
    addInputSerial: () => void
}

const ButtonUniqueNumber: React.FC <ButtonUniqueNumberProps> = ({ addInputSerial}) => {
    return (
        <div className='flex justify-center w-full my-20'>
            <ButtonPrimary onClick={addInputSerial} type='button'>
                <TwinTrans transKey={'add'}>Añadir</TwinTrans>
            </ButtonPrimary>
        </div>
    )
}

export default ModalUniqueNumberProduct